import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  ActionMenu,
  Badge,
  Breaker,
  Button,
  Flexbox,
  InputField,
  StyledLink,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import { formatBookingTime } from "../../../utils/util";
import {
  ArrowLeft,
  ArrowRight,
  Calender,
  Clock,
  Close,
  CloseCircle,
  CrossSqr,
  OptionVerticle,
  Search,
  TickSqr,
  Users,
} from "../../Theme/Icons";
import { ToolTip } from "../../Components/ToolTip/ToolTip";
import { PopupBody, PopupWrapper } from "../../Components/Popup/CommonPopup";
import InitialsAvatar from "../../Components/InitialsAvatar";
import CustomCalendar from "../../Components/CustomCalendar";
import {
  classroomData,
  clearClassroomSliceData,
  clearClassroomSliceStates,
  getAllPendingClassroomRequests,
  approveClassroomRequest,
  rejectClassroomRequest,
  revokeClassroomRequest,
  bulkStatusChangeClassroomRequest,
} from "../../../redux/classroom/classroomSlice";
import {
  cancelABooking,
  clearCoworkBookingsList,
  clearCoworkSliceData,
  coworkData,
  getAllBookings,
  getAllCoworkUsers,
  getAllRooms,
  getAllTowers,
} from "../../../redux/cowork/coworkSlice";
import CancelBookingPopup from "../../Components/Popup/CancelBookingPopup";
import BookingPreviewPopup from "../../Components/Popup/BookingPreviewPopup";
import { MODULES } from "../../../redux/appModule/appModuleSlice";
import useLatestRequest from "../../../utils/CustomHooks/useLatestRequest";

const PER_PAGE = 10;

const STATUS_OPTIONS = [
  { dropmenu: "All Statuses", value: "" },
  { dropmenu: "Under Review", value: "Under Review" },
  { dropmenu: "Approved", value: "Approved" },
  { dropmenu: "Rejected", value: "Rejected" },
  { dropmenu: "Cancelled", value: "Cancelled" },
];

const isPendingStatus = (s) => s === "Pending" || s === "Under Review";

// A booking can only be cancelled while it is still live.
const isCancellableStatus = (s) => s === "Approved" || s === "Booked";

const CHECKBOX_STYLE = {
  width: 16,
  height: 16,
  cursor: "pointer",
  accentColor: globalTheme.colors.primary,
};

const SKELETON_BAR = {
  display: "block",
  height: 14,
  borderRadius: 6,
  background:
    "linear-gradient(90deg, #f0f0f0 25%, #e3e3e3 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite",
};

// Shimmer row for the bookings table. The optional columns must match the
// header exactly — a short row would leave the shimmer misaligned under it.
const BookingSkeletonRow = ({ withCheckbox, withPurpose }) => (
  <tr>
    {withCheckbox && (
      <td>
        <div className="tabBox">
          <span style={{ ...SKELETON_BAR, width: 16, height: 16, borderRadius: 4 }} />
        </div>
      </td>
    )}
    <td>
      <div className="tabBox">
        <span style={{ ...SKELETON_BAR, width: 50 }} />
      </div>
    </td>
    <td>
      <div className="tabBox">
        <span style={{ ...SKELETON_BAR, width: 28, height: 28, borderRadius: "50%" }} />
        <span style={{ ...SKELETON_BAR, width: 110 }} />
      </div>
    </td>
    {/* Room, [Purpose], Date & Time, Attendees/Updated By, Status */}
    {[120, ...(withPurpose ? [160] : []), 150, 50, 80].map((w, i) => (
      <td key={i}>
        <div className="tabBox">
          <span style={{ ...SKELETON_BAR, width: w }} />
        </div>
      </td>
    ))}
    <td>
      <div className="tabBox">
        <span style={{ ...SKELETON_BAR, width: 28, height: 28, borderRadius: 12 }} />
      </div>
    </td>
  </tr>
);


const checkFor30MinTimeBeforeBooking = (booking_start_time) => {
  return (
    new Date() >
    new Date(booking_start_time.slice(0, -1)).setMinutes(
      new Date(booking_start_time.slice(0, -1)).getMinutes() - 30
    )
  );
};

const statusBadgeProps = (status,startTime) => {
  if(checkFor30MinTimeBeforeBooking(startTime) && status === 'Booked') return { green: true };
  if(status === 'Booked') return {blue : true};
  if (status === "Approved") return { green: true };
  if (status === "Rejected" || status === "Cancelled") return { red: true };
  return { yellow: true };
};

const Bookings = () => {
  const dispatch = useDispatch();
  // Fetches go through this instead of `dispatch` so a re-fired request cancels
  // the one it supersedes (see useLatestRequest).
  const runLatest = useLatestRequest();
  const location = useLocation();
  const preselectedRoomId = location.state?.roomId || "";
  const preselectedRoomName = location.state?.roomName || "";
  // Optional slot deep-link from the Eduspace calendar's "View Requests" action:
  // naive UTC "YYYY-MM-DD HH:mm:ss" bounds → show requests overlapping that slot.
  const preselectedStartTime = location.state?.startTime || "";
  const preselectedEndTime = location.state?.endTime || "";

  const {
    isClassroomSliceFetching,
    isClassroomSliceSmallFetching,
    isClassroomSliceSuccess,
    isClassroomSliceError,
    classroomSliceSuccessMessage,
    classroomSliceErrorMessage,
    allPendingRequestsList,
    allPendingRequestsCount,
  } = useSelector(classroomData);
  
  const { AllBookingList, AllBookingCount, isCoworkSliceFetching, isgettingAllbookings} = useSelector(coworkData);

  const { AllRooms, towersList, coworkUsers } = useSelector(coworkData);
  const { selectedModule } = useSelector((state) => state.appModule);
  const isCowork = selectedModule === MODULES.COWORK;
  // Bloomberg books the terminal room per seat, so the number that matters to a
  // reviewing admin is how many seats the request takes — not who was invited.
  const isBloomberg = selectedModule === MODULES.BLOOMBERG;
  // Eduspace is the only module that shows the booking's purpose in the list —
  // classroom requests carry a meaningful one, the others rarely do.
  const isEduspace = selectedModule === MODULES.EDUSPACE;
  // Cowork bookings load through the cowork slice; classroom/lab requests through
  // the classroom slice. Each sets its own fetching flag, so the skeleton must
  // watch whichever slice is actually serving this module's list.
  const isListFetching = isCowork ? isgettingAllbookings : isClassroomSliceFetching;

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    tower: "",
    capacity: "",
    availability: "",
    room: preselectedRoomId,
    createdBy: "",
    status: "",
    startTime: preselectedStartTime,
    endTime: preselectedEndTime,
    // Single-day filter. Two keys for one control: the classroom endpoint reads
    // `date`, the cowork one already had `meetingDate` — writing both keeps the
    // picker working across modules without branching at every call site.
    date: "",
    meetingDate: "",
  });
  const [dateOpen, setDateOpen] = useState(false);
  const slotFilterActive = !!(filters.startTime && filters.endTime);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Booking preview (details + comments) — Makers Lab / Eduspace requests only,
  // where the comment API is available.
  const [previewRequest, setPreviewRequest] = useState(null);

  // Bulk selection (classroom / lab requests only).
  //
  // Selection spans PAGES, but a page swap replaces the rows in the store — so
  // the row behind a selected id is gone the moment you turn the page. Keeping
  // ids alone meant every check that had to read a row (is it pending? is it
  // rejectable?) silently dropped the off-page selections: the bar said "6
  // selected" while the button said "Reject (2)", and only those 2 were ever
  // sent. So each row is snapshotted as it is selected, and every downstream
  // check reads the snapshot.
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState({}); // id -> row snapshot
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkActionType, setBulkActionType] = useState(null);

  // Debounced search — also resets pagination so we don't land on an empty page.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch lookup data once for the filter dropdowns (rooms, towers, users).
  useEffect(() => {
    dispatch(clearCoworkSliceData());
    runLatest(getAllRooms, {
      perPageCount: 1000,
      pageNumber: 1,
      searchValue: "",
      filters: { tower: "", capacity: "", availability: "" },
      isClassAdmin: selectedModule === MODULES.EDUSPACE,
      organizationId: null,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    });
    runLatest(getAllTowers, {
      isCLassAdmin: selectedModule === MODULES.EDUSPACE,
      isLab: selectedModule === MODULES.MAKERSLAB,
      isBmb: selectedModule === MODULES.BLOOMBERG,
    });
    runLatest(getAllCoworkUsers, {
      isLabStudent: selectedModule === MODULES.MAKERSLAB,
      isBmbStudent: selectedModule === MODULES.BLOOMBERG,
    });
    return () => {
      dispatch(clearCoworkSliceData());
    };
  }, [dispatch, runLatest, selectedModule]);

  // Fetch requests whenever filters/search/page change. The slice APPENDS on
  // fulfilled, so we must clear before each fetch — but ONLY the bookings list.
  // This used to call clearCoworkSliceData(), which also empties `AllRooms`,
  // the list feeding the Room dropdown: picking a room re-ran this effect and
  // wiped the dropdown's own options, so it went blank the moment it was used
  // (and stayed blank, since rooms are only fetched on a module change).
  useEffect(() => {
    dispatch(clearClassroomSliceData());
    dispatch(clearCoworkBookingsList());
    isCowork ?
    runLatest(getAllBookings, {
      perPageCount: PER_PAGE,
      pageNumber: currentPage,
      searchValue: searchQuery,
      filters: filters,
      isClassroom : selectedModule === MODULES.EDUSPACE,
      isLab : selectedModule === MODULES.MAKERSLAB,
      isBmb : selectedModule === MODULES.BLOOMBERG,
    }) :
    runLatest(getAllPendingClassroomRequests, {
      perPageCount: PER_PAGE,
      pageNumber: currentPage,
      searchQuery,
      filters,
      isLab : selectedModule === MODULES.MAKERSLAB,
      isBmb : selectedModule === MODULES.BLOOMBERG,
    })
    return () => {
      dispatch(clearClassroomSliceStates());
      dispatch(clearClassroomSliceData());
    };
  }, [dispatch, runLatest, searchQuery, filters, currentPage,selectedModule]);

  // Error toast.
  console.log("Error?", isClassroomSliceError, classroomSliceErrorMessage);
  useEffect(() => {
    if (isClassroomSliceError) {
      toast.error(classroomSliceErrorMessage, {
        autoClose: 3000,
      });
      setActionLoading({});
      setBulkActionType(null);
      dispatch(clearClassroomSliceStates());
    }
  }, [isClassroomSliceError, classroomSliceErrorMessage, dispatch]);

  // Success toast + refresh after approve/reject.
  useEffect(() => {
    if (!isClassroomSliceSuccess) return;
    if (
      isClassroomSliceSuccess
    ) {
      toast.success(classroomSliceSuccessMessage, {
        autoClose: 3000,
      });
      setActionLoading({});
      setSelectedIds([]);
      setSelectedRows({});
      setBulkActionType(null);
      dispatch(clearClassroomSliceStates());
      dispatch(clearClassroomSliceData());
      // Bookings only — same reason as the fetch effect above: a blanket clear
      // here would empty the Room dropdown after every approve/reject.
      dispatch(clearCoworkBookingsList());
      isCowork ?
      runLatest(getAllBookings, {
        perPageCount: PER_PAGE,
        pageNumber: currentPage,
        searchValue: searchQuery,
        filters: filters,
      }) :
      runLatest(getAllPendingClassroomRequests, {
        perPageCount: PER_PAGE,
        pageNumber: currentPage,
        searchQuery,
        filters,
        isLab: selectedModule === MODULES.MAKERSLAB,
        isBmb: selectedModule === MODULES.BLOOMBERG,
      });
    }
  }, [isClassroomSliceSuccess, classroomSliceSuccessMessage]);

  const roomOptions = useMemo(() => {
    const list = (AllRooms || []).map((r) => ({
      dropmenu: r?.room_name,
      value: r?.id,
    }));
    return [{ dropmenu: "All Rooms", value: "" }, ...list];
  }, [AllRooms]);

  const towerOptions = useMemo(() => {
    const list = (towersList || []).map((t) => ({
      dropmenu: t?.tower,
      value: t?.id ?? t?.tower,
    }));
    return [{ dropmenu: "All Towers", value: "" }, ...list];
  }, [towersList]);

  const createdByOptions = useMemo(() => {
    const list = (coworkUsers || []).map((u) => {
      const fullName = `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim();
      const label = fullName || u?.officialEmail || "Unnamed user";
      return { dropmenu: label, value: u?.id };
    });
    return [{ dropmenu: "Created By", value: "" }, ...list];
  }, [coworkUsers]);

  const pendingCount = useMemo(
    () => (allPendingRequestsList || []).filter((r) => isPendingStatus(r.status)).length,
    [allPendingRequestsList]
  );

  // The list currently on screen — cowork bookings or classroom requests.
  const bookingData = useMemo(
    () =>
      AllBookingList?.length
        ? AllBookingList
        : allPendingRequestsList?.length
        ? allPendingRequestsList
        : [],
    [AllBookingList, allPendingRequestsList]
  );


  // A selected row, wherever it lives: the page on screen first (freshest
  // status), else the snapshot taken when it was ticked on an earlier page.
  const rowForSelectedId = (id) =>
    bookingData.find((r) => r.id === id) || selectedRows[id] || null;

  const pendingSelectedIds = selectedIds.filter((id) =>
    isPendingStatus(rowForSelectedId(id)?.status)
  );
  const rejectableSelectedIds = selectedIds.filter((id) => {
    const status = rowForSelectedId(id)?.status;
    return isPendingStatus(status) || status === "Approved";
  });
  const revokableSelectedIds = selectedIds.filter((id) => {
    const r = rowForSelectedId(id);
    return r && !isPendingStatus(r.status);
  });
  // Scoped to the page on screen — the header checkbox is "select everything
  // here", not "everything in 209 pages".
  const allOnPageSelected =
    bookingData.length > 0 && bookingData.every((r) => selectedIds.includes(r.id));

  const toggleSelectRow = (row) => {
    const id = row?.id ?? row;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setSelectedRows((prev) => {
      const next = { ...prev };
      // Keep the snapshot only while the row is selected, so the map can't grow
      // without bound as the admin pages around.
      if (next[id]) delete next[id];
      else if (row && typeof row === "object") next[id] = row;
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(bookingData.map((r) => r.id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
      setSelectedRows((prev) => {
        const next = { ...prev };
        pageIds.forEach((id) => delete next[id]);
        return next;
      });
      return;
    }
    // Add this page to whatever is already selected elsewhere, don't replace it.
    setSelectedIds((prev) => [...new Set([...prev, ...bookingData.map((r) => r.id)])]);
    setSelectedRows((prev) => {
      const next = { ...prev };
      bookingData.forEach((r) => { next[r.id] = r; });
      return next;
    });
  };
  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedRows({});
  };

  // Selected rows ride at the top of the table so a selection made three pages
  // ago stays visible (and untickable) instead of vanishing behind the paging.
  const visibleRows = useMemo(() => {
    if (isCowork || !selectedIds.length) return bookingData;
    const byId = new Map(bookingData.map((r) => [r.id, r]));
    const pinned = selectedIds
      .map((id) => byId.get(id) || selectedRows[id])
      .filter(Boolean);
    const pinnedIds = new Set(pinned.map((r) => r.id));
    return [...pinned, ...bookingData.filter((r) => !pinnedIds.has(r.id))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingData, selectedIds, selectedRows, isCowork]);
  // Which pinned rows came from another page — flagged in the row so the admin
  // can tell them apart from the page they are actually on.
  const offPageSelectedIds = useMemo(() => {
    const pageIds = new Set(bookingData.map((r) => r.id));
    return new Set(selectedIds.filter((id) => !pageIds.has(id)));
  }, [bookingData, selectedIds]);

  const totalPages = Math.max(1, Math.ceil(((isCowork ? AllBookingCount : allPendingRequestsCount) || 0) / PER_PAGE));

  const selectedRejectingRequest = (allPendingRequestsList || []).find((r) => r.id === rejectingId?.id) || (AllBookingList || []).find((r) => r.id === rejectingId?.id);

  // Build a windowed page list: always shows first/last page, current page with
  // one neighbour on each side, and "…" gaps where pages are skipped.
  const pageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const items = [1];
    if (currentPage > 3) items.push("…");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) items.push(i);
    if (currentPage < totalPages - 2) items.push("…");
    items.push(totalPages);
    return items;
  }, [currentPage, totalPages]);

  // "Cancel Booking" is implemented as a reject (status → Rejected) so it uses an
  // existing enum value and renders correctly. The backend also cancels the
  // confirmed booking so the slot frees up.
  const handleRevokeClick = (requestId) => {
    setActionLoading({ [requestId]: "cancel" });
    dispatch(rejectClassroomRequest({
      requestId,
      reason_for_rejection: "Cancelled by admin",
      status: "Rejected",
    }))
  };

  const updateFilter = (patch) => {
    setCurrentPage(1);
    setFilters((p) => ({ ...p, ...patch }));
  };

  // Drop the slot deep-link (room + time window) and go back to the full list.
  const clearSlotFilter = () =>
    updateFilter({ room: "", startTime: "", endTime: "" });

  const handleApprove = (id) => {
    setActionLoading({ [id]: "approve" });
    dispatch(approveClassroomRequest({ requestId: id }));
  };

  const openRejectModal = (id, type = "reject") => {
    setRejectingId({ id, type });
    setRejectionReason("");
  };

  const submitReject = () => {
    if (!rejectionReason.trim()) {
      toast.error("Please enter a rejection reason", {
        autoClose: 3000,
      });
      return;
    }

    const { id, type } = rejectingId || {};
    if (!id) return;

    const actionType = type === "cancel" ? "cancel" : "reject";
    setActionLoading({ [id]: actionType });

    if (type === "cancel") {
      dispatch(cancelABooking({ id }));
    } else {
      dispatch(
        rejectClassroomRequest({
          requestId: id,
          reason_for_rejection: rejectionReason.trim(),
          status : "Rejected"
        })
      );
    }

    setRejectingId(null);
    setRejectionReason("");
  };

  // ---- Bulk actions (approve / reject / revoke) ----
  // Each action sends exactly the ids its button counted — sending the whole
  // selection meant a mixed selection (some already approved, some rejected)
  // was pushed through wholesale, so the button said "Approve (2)" and the API
  // was handed all 6.
  const handleBulkApprove = () => {
    if (!pendingSelectedIds.length) return;
    setBulkActionType("approve");
    dispatch(
      bulkStatusChangeClassroomRequest({
        requestIds: pendingSelectedIds,
        action: "approved",
        reason_for_rejection: "",
      })
    );
  };

  const handleBulkRevoke = () => {
    if (!revokableSelectedIds.length) return;
    setBulkActionType("revoke");
    dispatch(
      bulkStatusChangeClassroomRequest({
        requestIds: revokableSelectedIds,
        action: "revoke",
        reason_for_rejection: "",
      })
    );
  };

  const openBulkReject = () => {
    if (!rejectableSelectedIds.length) return;
    setBulkRejectReason("");
    setBulkRejectOpen(true);
  };

  const submitBulkReject = () => {
    if (!bulkRejectReason.trim()) {
      toast.error("Please enter a rejection reason", {
        autoClose: 3000,
      });
      return;
    }
    setBulkActionType("reject");
    dispatch(
      bulkStatusChangeClassroomRequest({
        
        requestIds: rejectableSelectedIds,
        action: "rejected",
        reason_for_rejection: bulkRejectReason.trim(),
      })
    );
    setBulkRejectOpen(false);
    setBulkRejectReason("");
  };

  return (
    <WrapperBox background={'transparent'}>
      <BookingPreviewPopup
        showActive={!!previewRequest}
        request={previewRequest}
        onClose={() => setPreviewRequest(null)}
      />

      <CancelBookingPopup
        showActive={!!rejectingId}
        mode={rejectingId?.type === "cancel" ? "cancel" : "reject"}
        roomName={selectedRejectingRequest?.room_name}
        onCancel={() => {
          setRejectingId(null);
          setRejectionReason("");
        }}
        onConfirm={submitReject}
        reason={rejectionReason}
        setReason={setRejectionReason}
        bookedBy={`${selectedRejectingRequest?.requestingUser?.firstName || ""} ${selectedRejectingRequest?.requestingUser?.lastName || ""}`.trim()}
        bookingDate={moment.utc(selectedRejectingRequest?.start_time).format("MMM DD, YYYY")}
        bookingTime={`${moment.utc(selectedRejectingRequest?.start_time).format("hh:mm A")} - ${moment
          .utc(selectedRejectingRequest?.end_time)
          .format("hh:mm A")}`}
        duration={moment
          .utc(selectedRejectingRequest?.end_time)
          .diff(moment.utc(selectedRejectingRequest?.start_time), "minutes")} mins
        isPopupFetching={actionLoading[rejectingId?.id] === (rejectingId?.type === "cancel" ? "cancel" : "reject")}
      />

      {/* Bulk reject — one reason applied to all selected pending requests. */}
      <PopupWrapper className={bulkRejectOpen ? "active" : ""}>
        <PopupBody maxWidth={480} padding={24}>
          <Flexbox spaceBW alignCenter>
            <Text font18 inter semiBold white>
              Reject {rejectableSelectedIds.length} Request{rejectableSelectedIds.length > 1 ? "s" : ""}
            </Text>
            <CloseCircle
              className="pointer"
              onClick={() => {
                setBulkRejectOpen(false);
                setBulkRejectReason("");
              }}
            />
          </Flexbox>
          <Flexbox
            style={{
              height: 1,
              background: globalTheme.colors.white1,
              opacity: 0.18,
              margin: "16px 0",
            }}
          />
          <Text font14 inter medium white className="mb16">
            The same reason will be applied to all {rejectableSelectedIds.length} selected
            request{rejectableSelectedIds.length > 1 ? "s" : ""}.
          </Text>
          <InputField
            label="Reason for rejection"
            placeholder="Enter rejection reason"
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
          />
          <Flexbox alignCenter gap={12} justiCenter className="mt20">
            <Button
              buttonMd
              whiteFilled
              name="Cancel"
              onClick={() => {
                setBulkRejectOpen(false);
                setBulkRejectReason("");
              }}
            />
            <Button buttonMd redFilled name="Confirm Rejection" onClick={submitBulkReject} />
          </Flexbox>
        </PopupBody>
      </PopupWrapper>

      <WrapperBox borderColor={globalTheme.colors.nuetral2} borderRadius={20} padding={'0'} boxShadow>
        <Flexbox spaceBW alignCenter padding='20px'>
          <Flexbox maxWidth='max-content' gap={12} alignCenter>
            <Text font16 inter medium black4>All Booking Requests</Text>
            {/* <Badge statusText={'10 new request'} red borderRadius='20px' /> */}
          </Flexbox>
          <Flexbox maxWidth='70%' gap={12} alignCenter flexEnd>
            <InputField
              noLabel
              hasIcon={<Search />}
              height='40px'
              placeholder='Search by ticket, purpose, name ...'
              revIcon
              maxWidth='300px'
              minWidth='250px'
              autoFocus
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            />

            <WrapperBox background={globalTheme.colors.nuetral2} maxWidth='max-content' padding={'12px'} borderRadius={50} borderColor={globalTheme.colors.nuetral2} maxHeight={'max-content'} className={'pointer'} overflow={'visible'}>

              <ActionMenu btnName="Select Tower"
                menuList={towerOptions} 
                onSelect={(value) => updateFilter({ tower: value })}
              />
            </WrapperBox>

            <WrapperBox background={globalTheme.colors.nuetral2} maxWidth='max-content' padding={'12px'} borderRadius={50} borderColor={globalTheme.colors.nuetral2} maxHeight={'max-content'} className={'pointer'} overflow={'visible'}>

              <ActionMenu
                key={preselectedRoomName || "select-room"}
                btnName={preselectedRoomName || "Select Room"}
                menuList={roomOptions}
                onSelect={(value) => updateFilter({ room: value })}
              />
            </WrapperBox>

            <WrapperBox
              background={globalTheme.colors.nuetral2}
              maxWidth="max-content"
              padding={"12px"}
              borderRadius={50}
              borderColor={globalTheme.colors.nuetral2}
              maxHeight={"max-content"}
              className={"pointer"}
              overflow={"visible"}
            >
              <ActionMenu
                btnName="All Statuses"
                menuList={STATUS_OPTIONS}
                onSelect={(value) => updateFilter({ status: value })}
              />
            </WrapperBox>

            {/* Single-day filter. Anchored relative so the calendar pops over
                the table instead of stretching the toolbar. */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <WrapperBox
                background={globalTheme.colors.nuetral2}
                maxWidth="max-content"
                padding={"12px"}
                borderRadius={50}
                borderColor={globalTheme.colors.nuetral2}
                maxHeight={"max-content"}
                className={"pointer"}
                overflow={"visible"}
                onClick={() => setDateOpen((o) => !o)}
              >
                {/* lineHeight:1 on the label and a fixed-size clear button keep
                    this pill exactly as tall as the ActionMenu chips beside it —
                    the icons, not the padding, would otherwise set the height. */}
                <Flexbox gap={6} alignCenter maxWidth="max-content" noWrap>
                  <span style={{ display: "flex", flexShrink: 0 }}>
                    <Calender color={globalTheme.colors.nuetral7} />
                  </span>
                  <Text
                    font14
                    inter
                    medium
                    black4
                    style={{ whiteSpace: "nowrap", lineHeight: 1 }}
                  >
                    {filters.date
                      ? moment(filters.date, "YYYY-MM-DD").format("MMM DD, YYYY")
                      : "Any date"}
                  </Text>
                  {filters.date && (
                    <span
                      title="Clear date"
                      onClick={(e) => {
                        // Don't reopen the calendar on the way out.
                        e.stopPropagation();
                        setDateOpen(false);
                        updateFilter({ date: "", meetingDate: "" });
                      }}
                      // `Close` is an 11px glyph — too small to hit on its own,
                      // so it gets an 18px circular target with a hover tint.
                      // (CloseCircle, the obvious pick, is a 31×32 SVG with a
                      // baked-in grey disc: it made the pill taller than every
                      // other filter and pushed the label off-centre.)
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        flexShrink: 0,
                        marginLeft: 2,
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = globalTheme.colors.grey6;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <Close />
                    </span>
                  )}
                </Flexbox>
              </WrapperBox>
              {dateOpen && (
                <CustomCalendar
                  align="right"
                  selectedDate={filters.date ? new Date(`${filters.date}T00:00:00`) : null}
                  onSelectDate={(str) => {
                    updateFilter({ date: str, meetingDate: str });
                    setDateOpen(false);
                  }}
                  onClose={() => setDateOpen(false)}
                />
              )}
            </div>
          </Flexbox>
        </Flexbox>

        {/* Slot deep-link banner — shown when arriving from the Eduspace calendar's
            "View Requests" action; lists requests overlapping a specific room + slot. */}
        {slotFilterActive && (
          <Flexbox
            spaceBW
            alignCenter
            padding="12px 20px"
            background={globalTheme.colors.grey8}
            style={{ borderTop: `1px solid ${globalTheme.colors.nuetral2}` }}
          >
            <Text font13 inter medium black4>
              Showing requests for
              {preselectedRoomName ? ` ${preselectedRoomName} ·` : " the selected room ·"}{" "}
              {moment.utc(filters.startTime).format("MMM DD, YYYY")} ·{" "}
              {moment.utc(filters.startTime).format("hh:mm A")} – {moment.utc(filters.endTime).format("hh:mm A")}
            </Text>
            <Text
              font13
              inter
              semiBold
              className="pointer"
              onClick={clearSlotFilter}
              style={{ color: globalTheme.colors.primary, textDecoration: "underline", whiteSpace: "nowrap" }}
            >
              Clear filter
            </Text>
          </Flexbox>
        )}

        {/* Bulk action bar — appears once rows are selected (requests only). */}
        {!isCowork && selectedIds.length > 0 && (
          <Flexbox
            spaceBW
            alignCenter
            padding="12px 20px"
            background={globalTheme.colors.grey8}
            style={{ borderTop: `1px solid ${globalTheme.colors.nuetral2}` }}
          >
            <Flexbox gap={10} alignCenter maxWidth="max-content">
              <Text font13 inter semiBold black4>
                {selectedIds.length} selected
              </Text>
              <Text
                font13
                inter
                medium
                grey2
                className="pointer"
                onClick={clearSelection}
                style={{ textDecoration: "underline" }}
              >
                Clear
              </Text>
            </Flexbox>
            <Flexbox gap={8} alignCenter maxWidth="max-content" flexEnd>
              <Button
                buttonSm
                revIcon
                icon={<TickSqr color={globalTheme.colors.green2} />}
                name={`Approve (${pendingSelectedIds.length})`}
                bgColor={globalTheme.colors.green1}
                color={globalTheme.colors.green2}
                onClick={handleBulkApprove}
                disabled={!pendingSelectedIds.length || isClassroomSliceSmallFetching}
              />
              <Button
                buttonSm
                revIcon
                icon={<CrossSqr color={globalTheme.colors.red} />}
                name={`Reject (${rejectableSelectedIds.length})`}
                bgColor={globalTheme.colors.primary1}
                color={globalTheme.colors.red}
                onClick={openBulkReject}
                disabled={!rejectableSelectedIds.length || isClassroomSliceSmallFetching}
              />
            </Flexbox>
          </Flexbox>
        )}

        <div className="tableWrapper">
          <div className="mainTable">
            <table>
              <thead>
                <tr>
                  {!isCowork && (
                    <th>
                      <div className="tabBox">
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={toggleSelectAll}
                          style={CHECKBOX_STYLE}
                          aria-label="Select all"
                        />
                      </div>
                    </th>
                  )}
                  <th>
                    <div className="tabBox">Booking ID</div>
                  </th>
                  <th>
                    <div className="tabBox">Name</div>
                  </th>
                  <th>
                    <div className="tabBox">Room</div>
                  </th>
                  {isEduspace && (
                    <th>
                      <div className="tabBox">Purpose</div>
                    </th>
                  )}
                  <th>
                    <div className="tabBox">Date & Time</div>
                  </th>
                  
                  {
                    selectedModule === MODULES.EDUSPACE ?
                    <th>
                      <div className="tabBox">Updated By</div>
                    </th> : isBloomberg ?
                    <th>
                      <div className="tabBox">Seats</div>
                    </th> : <th>
                      <div className="tabBox">Attendees</div>
                    </th>
                  }
                  <th>
                    <div className="tabBox">Status</div>
                  </th>
                  <th>
                    <div className="tabBox">Action</div>
                  </th>
                </tr>
              </thead>
              
              <tbody>
                {
                  (() => {
                    return visibleRows.length === 0 ? (
                    isListFetching ? (
                      [...Array(6)].map((_, i) => (
                        <BookingSkeletonRow key={i} withCheckbox={!isCowork} withPurpose={isEduspace} />
                      ))
                    ) : (
                    <tr>
                      <td colSpan={(!isCowork ? 8 : 7) + (isEduspace ? 1 : 0)}>
                        <Flexbox justiCenter alignCenter style={{ padding: "40px 0" }}>
                          <Text font14 inter regular grey2>
                            No booking requests found
                          </Text>
                        </Flexbox>
                      </td>
                    </tr>
                    )
                    ) : (
                  visibleRows.map((req) => {
                    const offPage = offPageSelectedIds.has(req.id);
                    const user = req.requestingUser || req.CoworkUser || {};
                    const updatedBy = req.lastUpdatedByUser || req.requestingUser || req.CoworkUser || {};
                    const dateStr = moment.utc(req.start_time).format("MMM DD, YYYY");
                    // Built from the request's 30-minute slot rows, so a booking
                    // of non-adjacent slots shows both ranges comma-separated
                    // instead of one span that swallows the gap.
                    const timeStr = formatBookingTime(req);
                    // Bloomberg rows report the seats the booking consumes (what
                    // decides whether the slot stays open); every other module
                    // reports how many people were invited.
                    const attendeesCount = isBloomberg
                      ? Math.max(1, Number(req?.participants) || 1)
                      : (req.attendees || []).length;
                    const pending = isPendingStatus(req.status);
                    // Only a live booking can be cancelled — once it is rejected or
                    // cancelled there is nothing to act on.
                    const cancellable = isCancellableStatus(req.status);
                    const loading = actionLoading[req.id];
                    return (
                      <tr
                        key={req.id}
                        // Pinned-from-another-page rows read as a distinct group
                        // rather than as rows of the page being viewed.
                        style={offPage ? { background: globalTheme.colors.grey8 } : undefined}
                      >
                        {!isCowork && (
                          <td>
                            <div className="tabBox">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(req.id)}
                                onChange={() => toggleSelectRow(req)}
                                style={CHECKBOX_STYLE}
                                aria-label={`Select request ${req?.ticket_id || req?.id}`}
                              />
                            </div>
                          </td>
                        )}
                        <td>
                          <div className="tabBox">
                            #{req?.ticket_id || req?.id || "—"}
                            {offPage && (
                              <Text
                                font10
                                inter
                                medium
                                nuetral6
                                style={{ marginLeft: 6, whiteSpace: "nowrap" }}
                              >
                                · selected
                              </Text>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            <InitialsAvatar firstName={user.firstName} lastName={user.lastName} />
                            <StyledLink
                              color={globalTheme.colors.black4}
                              fontWeights="500"
                              className={!isCowork ? "pointer" : ""}
                              onClick={() => !isCowork && setPreviewRequest(req)}
                            >
                              {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "—"}
                            </StyledLink>
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">{req?.room?.room_name || "—"}</div>
                        </td>
                        {isEduspace && (
                          <td>
                            <div className="tabBox">
                              {/* One line, clipped with an ellipsis so a long
                                  purpose can't stretch the column and push the
                                  rest of the row off-screen. The full text is on
                                  hover — ToolTip renders its child untouched
                                  when `content` is empty, so a booking with no
                                  purpose just shows the dash. */}
                              <ToolTip
                                position="Bottom"
                                maxWidth="320px"
                                content={req?.meeting_title || ""}
                              >
                                <Text
                                  font12
                                  inter
                                  regular
                                  nuetral6
                                  style={{
                                    display: "block",
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {req?.meeting_title || "—"}
                                </Text>
                              </ToolTip>
                            </div>
                          </td>
                        )}
                        <td>
                          <div className="tabBox">
                            <Flexbox gap={10} alignCenter flexEnd maxWidth="max-content">
                              <Flexbox gap={4} alignCenter maxWidth="max-content">
                                <Calender color={globalTheme.colors.nuetral6} />
                                <Text font12 inter regular nuetral6>
                                  {dateStr}
                                </Text>
                              </Flexbox>
                              <Flexbox gap={4} alignCenter maxWidth="max-content">
                                <Clock />
                                <Text font12 inter regular nuetral6>
                                  {timeStr}
                                </Text>
                              </Flexbox>
                            </Flexbox>
                          </div>
                        </td>
                        
                        {
                          selectedModule === MODULES.EDUSPACE ? 
                          <td>
                                {/* <InitialsAvatar firstName={updatedBy.firstName} lastName={updatedBy.lastName} /> */}
                                <div className="tabBox">{`${updatedBy.firstName || ""} ${updatedBy.lastName || ""}`.trim() || "—"}</div>
                          </td> : <td>
                            <div className="tabBox">
                              <Flexbox gap={4} alignCenter maxWidth="max-content">
                                <Users />
                                <Text font12 inter regular nuetral6>
                                  {String(attendeesCount).padStart(2, "0")}
                                </Text>
                              </Flexbox>
                            </div>
                          </td>
                        }
                        
                        <td>
                          <div className="tabBox">
                            <Flexbox gap={4} alignCenter maxWidth="max-content">
                              <Badge
                                  statusText={checkFor30MinTimeBeforeBooking(req.start_time) && req.status === 'Booked' ? 'Completed' : req.status}
                                  borderRadius={"20px"}
                                  small
                                  {...statusBadgeProps(req.status,req.start_time)}
                                />
                            </Flexbox>
                          </div>
                        </td>
                        <td>
                          <div className="tabBox">
                            <Flexbox gap={8}>
                              {pending ? (
                                <>
                                  <ToolTip position="Bottom" minWidth="max-content" content="Approve">
                                    <span
                                      onClick={() => !loading && handleApprove(req.id)}
                                      style={{
                                        opacity: loading ? 0.5 : 1,
                                        cursor: loading ? "not-allowed" : "pointer",
                                      }}
                                    >
                                      <Badge noText icon={<TickSqr />} borderRadius={"12px"} green />
                                    </span>
                                  </ToolTip>
                                  <ToolTip position="Bottom" minWidth="max-content" content="Reject">
                                    <span
                                      onClick={() => !loading && openRejectModal(req.id, "reject")}
                                      style={{
                                        opacity: loading ? 0.5 : 1,
                                        cursor: loading ? "not-allowed" : "pointer",
                                      }}
                                    >
                                      <Badge noText icon={<CrossSqr />} borderRadius={"12px"} red />
                                    </span>
                                  </ToolTip>
                                </>
                              ) : (
                                isCowork ? (
                                  cancellable && (
                                    <ToolTip position="Bottom" minWidth="max-content">
                                      <span
                                        style={{
                                          opacity: loading ? 0.5 : 1,
                                          cursor: loading ? "not-allowed" : "pointer",
                                        }}
                                      >
                                        <ActionMenu
                                          staticLabel
                                          btnName=""
                                          menuList={[
                                            {
                                              dropmenu: "Cancel Booking",
                                              action : () => openRejectModal(req.id, "cancel")
                                            },
                                          ]}
                                          toggleIcon={<OptionVerticle />}
                                        />
                                      </span>
                                    </ToolTip>
                                  )
                                ) : (
                                <ToolTip position="Bottom" minWidth="max-content">
                                  <div
                                    style={{
                                      opacity: loading ? 0.5 : 1,
                                      cursor: loading ? "not-allowed" : "pointer",
                                    }}
                                    className="flex"
                                  >
                                  <ActionMenu
                                    staticLabel
                                    btnName=""
                                    menuList={[
                                      {
                                        dropmenu: "View Booking",
                                        action: () => setPreviewRequest(req),
                                      },
                                      // A rejected/cancelled request has nothing left
                                      // to cancel — only an approved one does.
                                      ...(cancellable
                                        ? [
                                            {
                                              dropmenu: "Cancel Booking",
                                              action: () => handleRevokeClick(req.id),
                                            },
                                          ]
                                        : []),
                                    ]}
                                    toggleIcon={<OptionVerticle />}
                                  />
                                  </div>
                                </ToolTip>
                                )
                              )}
                            </Flexbox>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )
                  })()
                }
                
              </tbody>
            </table>
          </div>
        </div>

        <Breaker borderColor={globalTheme.colors.nuetral2} />
        <Flexbox spaceBW alignCenter padding="20px">
          <Button
            name="Previous"
            primaryOutlined
            buttonMd
            icon={<ArrowLeft />}
            revIcon
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          />
          <Flexbox gap={4} alignCenter justiCenter maxWidth="max-content">
            {pageItems.map((item, idx) =>
              item === "…" ? (
                <div
                  key={`gap-${idx}`}
                  style={{
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text font14 inter regular nuetral6>
                    …
                  </Text>
                </div>
              ) : (
                <div
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background:
                      item === currentPage
                        ? globalTheme.colors.nuetral2
                        : "transparent",
                  }}
                >
                  <Text
                    font14
                    inter
                    {...(item === currentPage
                      ? { medium: true, black4: true }
                      : { regular: true, nuetral6: true })}
                  >
                    {item}
                  </Text>
                </div>
              )
            )}
          </Flexbox>
          <Button
            name="Next"
            primaryOutlined
            buttonMd
            icon={<ArrowRight />}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        </Flexbox>
      </WrapperBox>
    </WrapperBox>
  );
};

export default Bookings;
